import {useCallback, useEffect, useState} from 'react';

import {kycService} from '@/services/kycService';
import type {
  AdminAfriexPerson,
  AfriexVirtualAccount,
  AfriexVirtualAccountApplicationData,
} from '@/types';
import {AfriexKYCProcessor, AfriexVerificationStatus} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {showSuccessMessage} from '@/utils/showSuccessMessage';

import type {KYCDetailsReturnProps} from './types';

const useKYCDetails = (userId: string): KYCDetailsReturnProps => {
  const [userDetails, setUserDetails] = useState<AdminAfriexPerson>(
    {} as AdminAfriexPerson,
  );
  const [isLoadingUserDetails, setIsLoadingUserDetails] = useState(false);

  const [showModal, setShowModal] = useState<boolean>(false);

  const [virtualAccountApplication, setVirtualAccountApplication] =
    useState<AfriexVirtualAccountApplicationData>(
      {} as AfriexVirtualAccountApplicationData,
    );

  const [virtualAccountInfo, setVirtualAccountInfo] =
    useState<AfriexVirtualAccount>({} as AfriexVirtualAccount);

  const toggleModal = () => setShowModal(!showModal);

  const fetchKYCDetails = useCallback(async (): Promise<void> => {
    try {
      if (!userId) {
        throw new Error('User Id is required');
      }
      setIsLoadingUserDetails(true);
      const [kycResponse, virtualAccountResponse, applicationsResponse] =
        // No `await` on the individual elements: awaiting inside the array
        // literal resolves each call before the next is even started, which
        // made these three run sequentially despite the Promise.all.
        await Promise.all([
          kycService.getKYCDetails(userId),
          kycService.getVirtualAccount(userId),
          kycService.getVirtualAccountApplications(userId),
        ]);
      setUserDetails(kycResponse);
      // Applications now live in their own collection rather than on
      // `person.metadata.virtualAccountApplication`, which the server no longer
      // writes. The endpoint returns them newest first, so [0] is the current
      // one -- the same single record this panel used to show, except a
      // rejected application is now visible too instead of disappearing.
      //
      // Flattened back into the panel's existing shape (intake leaves at the
      // top level, plus status/createdAt) so VirtualAccountApplication.tsx does
      // not have to change.
      const latestApplication = applicationsResponse?.[0];
      setVirtualAccountApplication(
        latestApplication
          ? {
              ...latestApplication.retailInfo,
              status: latestApplication.status,
              createdAt:
                latestApplication.appliedAt ?? latestApplication.createdAt,
              // VirtualAccountApplication.tsx renders an "Updated On" row from
              // this. The legacy subdocument carried its own timestamps, and
              // the new endpoint still puts `updatedAt` on the wire, so it has
              // to survive the flattening or the row renders blank.
              updatedAt: latestApplication.updatedAt,
            }
          : {},
      );
      // `getVirtualAccount` types its error-path return as `undefined`, but
      // that path already reports through the outer catch below (indexing
      // here throws the same way it did before this call was typed, and that
      // throw is what the catch below actually reports). Narrowing with `as`
      // rather than `?.` keeps that exact throw instead of silently turning
      // it into an `undefined` write.
      setVirtualAccountInfo(
        (virtualAccountResponse as AfriexVirtualAccount[])[0],
      );
    } catch (err) {
      const errorMessage = getErrorMessage(err, '');
      showErrorMessage('Failed to fetch KYC details:' + errorMessage);
    } finally {
      setIsLoadingUserDetails(false);
    }
  }, [userId]);

  const handleVerify = useCallback(
    async (shouldVerify: boolean): Promise<void> => {
      try {
        if (!userId) {
          throw new Error('User Id is required');
        }
        setIsLoadingUserDetails(true);
        const status = shouldVerify
          ? AfriexVerificationStatus.Success
          : AfriexVerificationStatus.Failed;
        await kycService.verifyUser(userId, status);
        showSuccessMessage('KYC status updated successfully');
      } catch (err) {
        const errorMessage = getErrorMessage(err, '');
        showErrorMessage('Failed to update KYC status:' + errorMessage);
      } finally {
        setIsLoadingUserDetails(false);
        await fetchKYCDetails();
      }
    },
    [userId],
  );

  const handleSubmission = useCallback(
    async (
      processor: AfriexKYCProcessor = AfriexKYCProcessor.Veriff,
    ): Promise<void> => {
      try {
        if (!userId) {
          throw new Error('User Id is required');
        }
        setIsLoadingUserDetails(true);
        await kycService.submitKYC(userId, processor);
        showSuccessMessage('KYC submitted successfully for verification');
      } catch (err) {
        const errorMessage = getErrorMessage(err, '');
        showErrorMessage(
          'Failed to submit KYC for verification:' + errorMessage,
        );
      } finally {
        setIsLoadingUserDetails(false);
      }
    },
    [userId],
  );

  const handleDeleteSession = useCallback(async (): Promise<void> => {
    try {
      if (!userId) {
        throw new Error('User Id is required');
      }
      setIsLoadingUserDetails(true);
      await kycService.deleteVeriffSesion(userId);
      showSuccessMessage('Veriff session successfully deleted');
    } catch (err) {
      const errorMessage = getErrorMessage(err, '');
      showErrorMessage('Failed to delete Veriff session' + errorMessage);
    } finally {
      setIsLoadingUserDetails(false);
    }
  }, [userId]);

  useEffect(() => {
    // Fire-and-forget: fetchKYCDetails try/catches, clears `isLoading` and
    // reports through showErrorMessage, so this only stops an unexpected
    // throw becoming an unhandled rejection.
    fetchKYCDetails().catch(() => {});
  }, [fetchKYCDetails]);

  return {
    handleVerify,
    handleSubmission,
    handleDeleteSession,
    isLoadingUserDetails,
    showModal,
    toggleModal,
    user: userDetails,
    virtualAccountApplication,
    virtualAccountInfo,
  };
};

export default useKYCDetails;
